import { isFiniteNumber } from "../../../../../../utils/number";
import { getSignalMarkerPassageXValues } from "../../../scheme/components/analytics/components/multiline-chart-signal/marker-points";
import type { IChartDataSignal } from "../../../scheme/components/analytics/interfaces";
import type { IOrbitRevolutionSelection } from "../../../multi-analysis/types";
import type { IOrbitBandSync } from "./band-selection";

const EMPTY_SYNC: IOrbitBandSync = {
  cursorValues: null,
  markerValues: null,
  datasetIndex: null,
};

export interface IRevolutionInterval {
  start: number;
  end: number;
}

export const revolutionBounds = (signal: IChartDataSignal | null | undefined): number[] => {
  if (!signal) {
    return [];
  }

  const markers = getSignalMarkerPassageXValues(signal);
  if (!markers.length) {
    return [];
  }

  const xEnd = signal.items[signal.items.length - 1]?.x;
  if (isFiniteNumber(xEnd) && xEnd > markers[markers.length - 1]) {
    return [...markers, xEnd];
  }

  return markers;
};

export const revolutionIntervalAt = (bounds: readonly number[], nativeX: number): IRevolutionInterval | null => {
  if (bounds.length < 2 || !isFiniteNumber(nativeX)) {
    return null;
  }

  for (let index = 0; index < bounds.length - 1; index += 1) {
    const start = bounds[index];
    const end = bounds[index + 1];
    const isLast = index === bounds.length - 2;
    if (nativeX >= start && (nativeX < end || (isLast && nativeX <= end))) {
      return { start, end };
    }
  }

  return null;
};

export const placeOrbitRevolutionCursor = (
  panelKey: string,
  nativeX: number,
  datasetIndex: number,
  signal: IChartDataSignal | null | undefined,
): IOrbitRevolutionSelection | null => {
  const interval = revolutionIntervalAt(revolutionBounds(signal), nativeX);
  if (!interval) {
    return null;
  }

  return {
    panelKey,
    cursorNativeX: nativeX,
    bandStartNativeX: interval.start,
    bandEndNativeX: interval.end,
    datasetIndex,
  };
};

export const shiftOrbitRevolutionCursor = (
  current: IOrbitRevolutionSelection | null,
  direction: -1 | 1,
  signal: IChartDataSignal | null | undefined,
): IOrbitRevolutionSelection | null => {
  if (!current || !signal?.items.length) {
    return null;
  }

  const bounds = revolutionBounds(signal);
  if (bounds.length < 2) {
    return null;
  }

  const duration = bounds[1] - bounds[0];
  if (!isFiniteNumber(duration) || duration <= 0) {
    return null;
  }

  const nextX = current.cursorNativeX + direction * duration;
  const xStart = signal.items[0]?.x;
  const xEnd = signal.items[signal.items.length - 1]?.x;
  if (!isFiniteNumber(xStart) || !isFiniteNumber(xEnd) || nextX < xStart || nextX > xEnd) {
    return null;
  }

  const interval = revolutionIntervalAt(bounds, nextX);
  if (!interval) {
    return null;
  }

  return {
    ...current,
    cursorNativeX: nextX,
    bandStartNativeX: interval.start,
    bandEndNativeX: interval.end,
  };
};

export const orbitRevolutionSyncValues = (
  datasetKeys: readonly string[],
  selection: IOrbitRevolutionSelection | null,
): IOrbitBandSync => {
  if (!selection) {
    return EMPTY_SYNC;
  }

  const cursorValues = datasetKeys.map(key => (key === selection.panelKey ? selection.cursorNativeX : null));
  if (!cursorValues.some(value => value != null)) {
    return EMPTY_SYNC;
  }

  const preferred = datasetKeys.findIndex(
    (key, index) => key === selection.panelKey && index === selection.datasetIndex,
  );
  const fallback = datasetKeys.findIndex(key => key === selection.panelKey);

  return {
    cursorValues,
    markerValues: null,
    datasetIndex: preferred >= 0 ? preferred : fallback >= 0 ? fallback : null,
  };
};

export const sameRevolutionBand = (
  current: IOrbitRevolutionSelection | null,
  next: IOrbitRevolutionSelection | null,
): boolean =>
  current != null &&
  next != null &&
  current.panelKey === next.panelKey &&
  current.bandStartNativeX === next.bandStartNativeX &&
  current.bandEndNativeX === next.bandEndNativeX;
